// REPORT and BLOCK of the game page (deployment.md §7.6, R1323, R1328):
// the ⋯ of a chat line of another player with its row of REPORT and
// BLOCK, the ⚑ beside a name of another player, the report card, and the
// lines the chat tabs hide after a BLOCK.
//
// The report card is a card of its own for the rename card's reason: the
// chat log and the tab body are rewritten when their markup changes, and
// a rewrite would take the box from under the finger. It is built once,
// when it opens. `ui/overlay.ts` hosts the card, draws the marks this desk
// writes and routes the presses here.

import { REPORT_NOTE_RUNES, REPORT_REASONS } from '../../gen/balance';
import type { CacheRow, ChatMsg, NamedSeamount } from '../../gen/proto/aquaphobia';
import { isWordName } from '../../seamounts';
import { hubReport as R, overlay as S } from '../../gen/strings';
import { messageOf } from '../../net/api';
import { valueChoice } from '../choice/choice';
import { esc } from '../esc';
import { ReportDraft, reasonLabel } from '../reportdraft';
import { el } from './markup';
import type { GameReport, UIActions } from './types';

/**
 * One piece the overlay reports (deployment.md §7.6, R1323): a piece of
 * the game server, or a clan chat line of the meta server by its sequence
 * number. `head` is the first line of the report card, with the author's
 * name already escaped.
 */
type ReportPiece = { head: string } & ({ game: GameReport } | { clanSeq: number });

/** The reason choice of the report card (R1323): eight reasons, so the
 *  chooser of the kit draws a select box, whose field reads REASON while
 *  no reason is picked (R2011). */
function reasonChoice(picked: string): string {
  return valueChoice({
    list: REPORT_REASONS.map((r) => ({ id: r, label: reasonLabel(r) })), chosen: picked,
    act: 'reportreason', label: R.reason_field, placeholder: R.reason_field, uid: 'reportreason',
  });
}

/** The intents a report and a block send. */
type ReportActions = Pick<UIActions, 'report' | 'reportClanLine' | 'blockAccount' | 'blockCommander'>;

/** The report form, the marks that open it and the blocks of this page. */
export class ReportDesk {
  /** The report card (R1323): the reason classes, a box for a note, SEND
   *  and CANCEL, for one chat line or one name of another player. */
  readonly el = el('div', 'panel', 'reportcard');
  /** The report form's state: the open piece, the reason, the note and
   *  the pieces reported. `ui/reportdraft.ts` holds it for the hub too. */
  private readonly draft = new ReportDraft<ReportPiece>();
  /** A clan chat report waits for the meta server's answer. */
  private busy = false;
  /** The chat line whose ⋯ menu is open, by its report key, or ''. */
  lineMenu = '';
  /** The public ids the CLAN tab hides after a BLOCK, and the line that
   *  stands under the chat after one: the confirmation or the refusal. */
  readonly blockedIds = new Set<string>();
  blockLine = '';
  /** The world account ids the WORLD, FACTION and SQUAD tabs hide after a
   *  BLOCK (R1328). The server hides the next line; this hides the lines
   *  the client already holds. */
  readonly blockedSeats = new Set<number>();

  /** `render` redraws the chrome after an answer; `selfId` is the seat's
   *  own account id: a control never reports its own piece. */
  constructor(
    private readonly actions: ReportActions,
    private readonly render: () => void,
    private readonly selfId: () => number,
  ) {
    this.el.hidden = true;
  }

  /** The report card stands. */
  get shown(): boolean { return !this.el.hidden; }

  /** A line of a commander this seat blocked is hidden (R1328). A scan
   *  line and a marker line carry the server's words, so no block hides
   *  them. */
  hidesLine(l: Pick<ChatMsg, 'fromId' | 'report' | 'marker'>): boolean {
    return this.blockedSeats.has(l.fromId) && !l.report && !l.marker;
  }

  /** The ⋯ control of one chat line of another player. */
  lineMore(key: string, piece: ReportPiece): string {
    this.draft.offer(key, piece);
    return `<button class="report more${this.lineMenu === key ? ' on' : ''}" data-act="linemore" `
      + `data-key="${key}" title="${S.line_more_title}" aria-label="${S.line_more_title}">`
      + `${S.line_more}</button>`;
  }

  /**
   * The row under a line whose ⋯ is open: REPORT, or REPORTED once a
   * report went out, and BLOCK. `block` is the attributes of the BLOCK
   * control: the sender's public id on the CLAN tab, which the meta
   * server takes, and the world account id on the three game channels,
   * which the game server takes (R1328).
   */
  lineMenuRow(key: string, block: string): string {
    if (this.lineMenu !== key) return '';
    const rep = this.draft.sent(key)
      ? `<span class="repd">${R.received_pill}</span>`
      : `<button class="report" data-act="reportopen" data-key="${key}">${S.line_report}</button>`;
    return `<div class="linemenu">${rep} <button class="report bad" ${block}>${S.line_block}</button></div>`;
  }

  /** The ⋯ of a line opens its row, and a second press shuts it. */
  toggleLineMenu(key: string): void {
    this.lineMenu = this.lineMenu === key ? '' : key;
  }

  /** The ⚑ mark beside a name of another player, or REPORTED once a
   *  report of the name went out. */
  mark(key: string, piece: ReportPiece): string {
    this.draft.offer(key, piece);
    if (this.draft.sent(key)) return `<span class="repd">${R.received_pill}</span>`;
    return `<button class="rep${this.draft.openKey === key ? ' primary' : ''}" data-act="reportopen" `
      + `data-key="${key}" title="${S.btn_report_name_title}" aria-label="${S.btn_report_name_title}">`
      + `${S.btn_report_name}</button>`;
  }

  /** The ⚑ mark of a commander, by the account id. */
  commanderMark(accountId: number, name: string): string {
    if (accountId === 0 || accountId === this.selfId()) return '';
    return this.mark(`cmdr:${accountId}`, {
      head: S.report_head_commander({ name: esc(name) }),
      game: { kind: 'commander', accountId },
    });
  }

  /**
   * The ⚑ mark of a seamount name (R2620, R2633), in a span the peek lets
   * a press reach. A name of the word list holds no words of a player and
   * the reader's own name is not reported, so both carry no mark, as a
   * hull with a faction pool name carries none (R1886).
   */
  seamountMark(n: NamedSeamount, selfName: string): string {
    if (!n.name || isWordName(n.name) || (selfName !== '' && n.commander === selfName)) return '';
    return `<span class="ck-smrep">${this.mark(`seamount:${n.body}`, {
      head: S.report_head_seamount({ name: esc(n.name) }),
      game: { kind: 'seamount', body: n.body },
    })}</span>`;
  }

  /**
   * The ⚑ mark of a cache note for its finder (v2 §9.4, R2631, R2649). Only
   * a free-text note carries it: a phrase cannot carry abuse, and the meta
   * server holds no copy of one. The note is never the reader's own,
   * because a commander finds no cache of their own.
   */
  cacheNoteMark(c: Pick<CacheRow, 'id' | 'note' | 'freeNote'>): string {
    if (!c.freeNote || !c.note) return '';
    return `<span class="ck-smrep">${this.mark(`cache:${c.id}`, {
      head: S.report_head_cache_note,
      game: { kind: 'cache', cache: c.id },
    })}</span>`;
  }

  /**
   * Opens the report card for one piece, or closes it when it is open for
   * that piece. The card is built once, when it opens, so the note box is
   * never rewritten under the finger.
   */
  open(key: string): void {
    const d = this.draft;
    if (d.openKey === key) { this.close(); return; }
    d.toggle(key);
    const p = d.pending();
    if (p === null) return;
    this.lineMenu = '';
    this.el.hidden = false;
    this.el.innerHTML = `<div class="ask">${p.target.head}</div>`
      + `<div class="hint">${esc(R.lede)}</div>`
      + `<div class="reasons">${reasonChoice('')}</div>`
      + `<input class="repin" type="text" autocomplete="off" maxlength="${REPORT_NOTE_RUNES}" `
      + `placeholder="${esc(R.note_box)}" aria-label="${esc(R.note_box)}">`
      + '<div class="err" hidden></div>'
      + '<div class="acts">'
      + `<button class="primary" data-act="reportsend">${esc(R.send)}</button>`
      + `<button data-act="reportcancel">${esc(R.cancel)}</button>`
      + '</div>';
    const box = this.el.querySelector<HTMLInputElement>('.repin');
    box?.addEventListener('input', () => { d.note = box.value; });
    box?.addEventListener('keydown', (e) => {
      e.stopPropagation(); // typing must never reach the map's key handler
      if (e.key === 'Enter') this.send();
      else if (e.key === 'Escape') this.close();
    });
  }

  /** Picks a reason class on the open card. The card is not rebuilt: the
   *  reason choice alone is written again, so the note box keeps its
   *  words and its caret. */
  pickReason(reason: string): void {
    if (!this.draft.pick(reason)) return;
    const box = this.el.querySelector<HTMLElement>('.reasons');
    if (box) box.innerHTML = reasonChoice(reason);
    this.error('');
  }

  /** Writes the card's one error line, or hides it for ''. */
  private error(text: string): void {
    const line = this.el.querySelector<HTMLElement>('.err');
    if (!line) return;
    line.textContent = text;
    line.hidden = text === '';
  }

  /**
   * SEND. A piece of the game server goes over the socket, and the server
   * sends no answer, so the control reads REPORTED at once. A clan chat
   * line goes to the meta server, and the card waits for its answer.
   */
  send(): void {
    const d = this.draft;
    const p = d.pending();
    if (p === null || this.busy) return;
    if (p.reason === '') { this.error(R.pick_reason); return; }
    const piece = p.target;
    if ('game' in piece) {
      this.actions.report(piece.game, p.reason, p.note);
      d.markSent(p.key);
      this.close();
      return;
    }
    this.busy = true;
    const send = this.el.querySelector<HTMLButtonElement>('button[data-act="reportsend"]');
    if (send) { send.disabled = true; send.textContent = R.busy; }
    void this.actions.reportClanLine(piece.clanSeq, p.reason, p.note).then(
      () => { d.markSent(p.key); if (d.openKey === '') this.close(); },
      (e: unknown) => { this.error(messageOf(e)); },
    ).finally(() => {
      this.busy = false;
      if (send) { send.disabled = false; send.textContent = R.send; }
      this.render();
    });
  }

  /** Takes the report card away. */
  close(): void {
    this.draft.close();
    this.el.hidden = true;
    this.el.innerHTML = '';
  }

  /** BLOCK one member in the clan chat. The meta server skips the lines
   *  from the next one on, and the CLAN tab hides the lines it holds. */
  blockSender(publicId: string): void {
    this.lineMenu = '';
    void this.actions.blockAccount(publicId).then(
      (ids) => {
        for (const id of ids) this.blockedIds.add(id);
        this.blockedIds.add(publicId);
        this.blockLine = S.line_blocked;
      },
      (e: unknown) => { this.blockLine = messageOf(e); },
    ).finally(() => this.render());
  }

  /** BLOCK one commander on the three game channels (R1328). The game
   *  server hides the next line, keeps the block in the account's one
   *  block list and sends no answer, so the tabs hide the lines they hold
   *  at once and say so. */
  blockSeat(accountId: number): void {
    this.lineMenu = '';
    if (accountId === 0 || accountId === this.selfId()) return;
    this.actions.blockCommander(accountId);
    this.blockedSeats.add(accountId);
    this.blockLine = S.line_blocked;
    this.render();
  }
}
